<script setup lang="ts">
// Stylised top-down table (net in the middle, two halves) — used on Home and New match
// (DESIGN.md section 9.1, screens 1-2). Each half is tappable; New match highlights the active
// side with a pulsing outline. Fixed (not aspect-ratio-driven) height: an aspect-ratio box inside
// a column flexbox that's also fighting for space with other rows (New match's format panel)
// gets squashed below its content's size and clips — a plain fixed height avoids that fight.
// `compact` is for New match, where the format panel needs most of the vertical budget at
// 960x540; Home has the room to show it bigger.
//
// 2026-08-31 (user feedback): Home became the new-game editor directly. `selectable` adds a
// small, subtly-styled "Select" pill below each side's avatar/placeholder — absolutely
// positioned so it never shifts the avatar's centered position — and suppresses the older
// "Tap to add" text label (the pill replaces it as the tap affordance). `showSwap` adds a small
// circular swap icon centered exactly on the table's center line. Both are opt-in and default
// off, so New match (which keeps its own header "⇄ Swap" + "Pick a name" flow) is unaffected.
//
// Round-25 (2026-08-31, user feedback: "one shared table-side design across Home and
// LiveMatch"): each side's box chrome (border weights, tint, corner dot, selection/glow rings)
// now comes from the shared TableSide.vue — the same component LiveMatch's score cards use —
// instead of a second copy of the same Tailwind classes. `cornerTheme` maps straight to
// TableSide's `theme` prop (off by default, so NewMatch is byte-for-byte unchanged); when on
// (Home only) it now gets LiveMatch's full corner-box treatment (border-2 + rounded + dot), not
// just the old top-border-only tint.
import { computed } from 'vue'
import type { Player } from '../types'
import Avatar from './Avatar.vue'
import TableSide from './TableSide.vue'

const props = withDefaults(
  defineProps<{
    leftPlayer?: Player | null
    rightPlayer?: Player | null
    activeSide?: 'left' | 'right' | null
    interactive?: boolean
    compact?: boolean
    selectable?: boolean
    showSwap?: boolean
    // Round-16 (2026-08-31): a brief, subtle glow on the side a voice corner-claim ("soy
    // rojo"/"soy azul") just assigned — dark minimal UI, so this is a soft tinted ring, not a
    // filled color block. NewMatch.vue clears it a few seconds after setting it.
    leftGlow?: boolean
    rightGlow?: boolean
    // Round-24 (2026-08-31, user feedback), escalated round-25: persistent red-left/blue-right
    // corner theming — now the SAME TableSide.vue box chrome LiveMatch's score cards use
    // (border-2 + rounded + tinted background/top-edge + corner dot), not just a top-border
    // tint. Opt-in and off by default — NewMatch keeps its current look; only Home turns this on.
    cornerTheme?: boolean
  }>(),
  {
    leftPlayer: null,
    rightPlayer: null,
    activeSide: null,
    interactive: false,
    compact: false,
    selectable: false,
    showSwap: false,
    leftGlow: false,
    rightGlow: false,
    cornerTheme: false,
  },
)

const emit = defineEmits<{ (e: 'select-side', side: 'left' | 'right'): void; (e: 'swap'): void; (e: 'clear-side', side: 'left' | 'right'): void }>()

// Only show "Tap to add" where tapping actually does something (New match); on Home the table
// is a static illustration. When `selectable` is on, the "Select" pill is the tap affordance
// instead, so the text label is suppressed to avoid showing both.
const leftLabel = computed(() => props.leftPlayer?.displayName ?? (props.interactive && !props.selectable ? 'Tap to add' : ''))
const rightLabel = computed(() => props.rightPlayer?.displayName ?? (props.interactive && !props.selectable ? 'Tap to add' : ''))
const avatarSize = computed(() => (props.compact ? 36 : 72))
</script>

<template>
  <div
    class="relative w-full shrink-0 rounded-2xl bg-table-felt border-4 border-emerald-950 overflow-hidden flex"
    :class="compact ? 'h-20' : 'h-56'"
  >
    <TableSide
      side="left"
      as="button"
      type="button"
      class="flex-1 gap-1 transition tap-target"
      :class="interactive ? 'active:bg-white/10' : ''"
      :theme="cornerTheme"
      :inset="cornerTheme"
      :active="activeSide === 'left'"
      :glow="leftGlow"
      @click="interactive && emit('select-side', 'left')"
    >
      <Avatar :player="leftPlayer" :size="avatarSize" />
      <span v-if="leftLabel" class="font-semibold truncate max-w-[90%]" :class="compact ? 'text-xs' : 'text-lg'">{{ leftLabel }}</span>
      <!-- Absolutely positioned (out of the flex flow) so it never shifts the avatar's centered
           position — see the file-level note above. -->
      <span v-if="selectable" class="absolute inset-x-0 bottom-3 flex justify-center pointer-events-none">
        <span class="px-3 py-1 rounded-full border border-white/20 text-[10px] font-semibold uppercase tracking-wide text-white/50">
          Select
        </span>
      </span>
      <!-- Round-9 (2026-08-31): clear-X on a filled side so a wrong pick can be removed without
           having to overwrite it. Plain span with @click.stop (not a nested <button> — invalid
           HTML inside the side's own button, and the stop keeps it from also opening the picker). -->
      <span
        v-if="selectable && leftPlayer"
        class="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white/70 active:bg-black/60 cursor-pointer"
        role="button"
        aria-label="Clear left player"
        @click.stop="emit('clear-side', 'left')"
      >✕</span>
    </TableSide>

    <div class="w-2 bg-table-line/80 self-stretch my-3 rounded"></div>

    <TableSide
      side="right"
      as="button"
      type="button"
      class="flex-1 gap-1 transition tap-target"
      :class="interactive ? 'active:bg-white/10' : ''"
      :theme="cornerTheme"
      :inset="cornerTheme"
      :active="activeSide === 'right'"
      :glow="rightGlow"
      @click="interactive && emit('select-side', 'right')"
    >
      <Avatar :player="rightPlayer" :size="avatarSize" />
      <span v-if="rightLabel" class="font-semibold truncate max-w-[90%]" :class="compact ? 'text-xs' : 'text-lg'">{{ rightLabel }}</span>
      <span v-if="selectable" class="absolute inset-x-0 bottom-3 flex justify-center pointer-events-none">
        <span class="px-3 py-1 rounded-full border border-white/20 text-[10px] font-semibold uppercase tracking-wide text-white/50">
          Select
        </span>
      </span>
      <!-- Round-9 (2026-08-31): clear-X on a filled side so a wrong pick can be removed without
           having to overwrite it. Plain span with @click.stop (not a nested <button> — invalid
           HTML inside the side's own button, and the stop keeps it from also opening the picker). -->
      <span
        v-if="selectable && rightPlayer"
        class="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white/70 active:bg-black/60 cursor-pointer"
        role="button"
        aria-label="Clear right player"
        @click.stop="emit('clear-side', 'right')"
      >✕</span>
    </TableSide>

    <button
      v-if="showSwap"
      type="button"
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-neutral-300 flex items-center justify-center text-base leading-none text-neutral-900 active:bg-neutral-200 shadow-md"
      aria-label="Swap sides"
      @click="emit('swap')"
    >
      ⇄
    </button>
  </div>
</template>
